import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { DeleteTagModal, TagModal } from "modules/TagsDashboard/components";
import "modules/TagsDashboard/components/TagsDashboardTable/TagsDashboardTable.scss";
import { useTagsQuery } from "modules/TagsDashboard/hooks";

import type { Tag } from "models";

const columnHelper = createColumnHelper<Tag>();

export const TagsDashboardTable = () => {
  const { t } = useTranslation();

  const { data: tags, isLoading: isTagsLoading } = useTagsQuery();

  const [tagToUpdate, setTagToUpdate] = useState<Tag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "name",
        size: 800,
        header: () => <span className="no-wrap-text">{t("v8_tag_name", "Tag name")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.name}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "is_system",
        size: 90,
        header: () => <span className="no-wrap-text">{t("v8_system", "System")}</span>,
        cell: ({ row: { original } }) => <Icon className={original.is_system ? "check green" : "xmark red"} />
      }),
      columnHelper.accessor(row => row, {
        id: "color",
        size: 90,
        header: () => <span className="no-wrap-text">{t("v8_color", "Color")}</span>,
        cell: ({ row: { original } }) => <span className="tag-color-swatch" style={{ backgroundColor: original.color }} />
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        size: 90,
        header: () => "",
        cell: ({ row: { original: tag } }) => (
          <div className="actions-container">
            <div className="action-edit-button-container" onClick={() => setTagToUpdate(tag)}>
              <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" />
            </div>
            <Can I="delete" the="tags">
              <div className="action-button-container" onClick={() => setTagToDelete(tag)}>
                <Icon className="trash action-button-icon-center" color="red" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t]
  );

  return (
    <>
      <div className="TagsDashboardTable">
        <ReactTable isFetching={isTagsLoading} columns={columns} data={tags} />
      </div>
      <TagModal isOpen={tagToUpdate !== null} tag={tagToUpdate} onClose={() => setTagToUpdate(null)} />
      <DeleteTagModal tag={tagToDelete} onClose={() => setTagToDelete(null)} />
    </>
  );
};
